import React, { useEffect, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import CompanyService from "../../services/CompanyService";
import SgsService from "../../services/SgsService";
import UserService from "../../services/UserService";
import Estados from "../../enums/Estados";

export default function CreateNewUserModal({ isOpen, setIsOpen, onUserCreated }) {
  const [companiesList, setCompaniesList] = useState([]);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    reset,
  } = useForm();

  useEffect(() => {
    if (isOpen) {
      CompanyService.getCompanies().then(result => setCompaniesList(result.companies || []));
      reset();
    }
  }, [isOpen, reset]);

  const watchPassword = watch('password');
  const watchReLegal = watch('reLegal');
  const isReLegalValid = /^80\d{6}$/.test(watchReLegal || "");
  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSearchTecnico = async () => {
    const reLegal = getValues("reLegal");

    if (!/^80\d{6}$/.test(reLegal || "")) {
      toast.error("Preencha um RE / LEGAL válido antes de buscar.");
      return;
    }

    try {
      const response = await SgsService.getTecnico(reLegal);

      if (response?.result?.data) {
        const tecnico = response.result.data;
        setValue("name", tecnico.nome || "", { shouldValidate: true, shouldDirty: true });
        setValue("phone", tecnico.telefone || "", { shouldValidate: true, shouldDirty: true });
        toast.success("Usuário encontrado no SGS");
      } else {
        toast.error("Usuário não encontrado no SGS.");
      }
    } catch (error) {
      toast.error("Ocorreu um erro ao buscar o usuário no SGS.");
    }
  };

  const onSubmit = async (data) => {
    try {
      const response = await UserService.createUser(data);

      if (!response) {
        toast.error("Erro ao criar usuário.");
        return;
      }

      toast.success("Usuário criado com sucesso!");
      setIsOpen(false);
      if (onUserCreated) onUserCreated();
    } catch (err) {
      toast.error(err?.message || "Erro ao criar usuário.");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="flex flex-col relative z-10"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto" onClick={() => setIsOpen(false)}>
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div
            className="flex flex-col max-w-[700px] w-full h-fit pb-4 relative transform rounded-lg bg-white text-left shadow-xl transition-all mt-6"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute -top-7 -right-7 rounded-full p-1"
            >
              <div className="w-12 h-12 flex bg-gray-200 justify-center items-center rounded-full ms-6">
                <div className="flex justify-center items-center">
                  <IoIosClose color={"black"} size={32} />
                </div>
              </div>
            </button>
            <div className="flex flex-col px-4 pt-3 pb-2 text-left gap-1 bg-gray-800 text-white rounded-t-lg">
              <div className="flex justify-center text-xl font-bold">
                Cadastrar Usuário
              </div>
            </div>
            <form className="flex flex-col gap-2 px-6 py-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="flex gap-2">
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-gray-900 mb-1">RE / LEGAL:</label>
                  <div className="flex items-center gap-2">
                    <input
                      {...register("reLegal", {
                        validate: (value) => !value || /^80\d{6}$/.test(value),
                      })}
                      type="text"
                      inputMode="numeric"
                      maxLength={8}
                      onInput={(event) => {
                        event.target.value = event.target.value.replace(/\D/g, "").slice(0, 8);
                      }}
                      className={`block w-full rounded-2xl px-4 py-0.5 text-sm text-gray-900 shadow-sm
                        ${errors?.reLegal ? "border border-red-500" : "border border-zinc-500"}
                      `}
                    />
                    <button
                      type="button"
                      onClick={handleSearchTecnico}
                      disabled={!isReLegalValid}
                      className={`px-4 py-0.5 text-sm rounded-2xl text-white whitespace-nowrap ${isReLegalValid ? "bg-blue-500 hover:bg-blue-600" : "bg-blue-300 cursor-not-allowed"}`}
                    >
                      Buscar
                    </button>
                  </div>
                  {errors?.reLegal && <p className="text-red-500 text-xs">Informe 8 dígitos iniciando com 80</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-gray-900 mb-1">Nome:</label>
                  <input
                    {...register("name", { required: true })}
                    type="text"
                    disabled={!isReLegalValid}
                    className={`block w-full rounded-2xl px-4 py-0.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 disabled:bg-gray-200
                      ${errors?.name ? "border border-red-500" : "border border-zinc-500"}
                    `}
                  />
                  {errors?.name && <p className="text-red-500 text-xs">Nome é obrigatório</p>}
                </div>
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-gray-900 mb-1">Email:</label>
                  <input
                    {...register("email", { required: true, validate: validateEmail })}
                    type="email"
                    disabled={!isReLegalValid}
                    className={`block w-full rounded-2xl px-4 py-0.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 disabled:bg-gray-200
                      ${errors?.email ? "border border-red-500" : "border border-zinc-500"}
                    `}
                  />
                  {errors?.email?.type === "required" && <p className="text-red-500 text-xs">Email é obrigatório</p>}
                  {errors?.email?.type === "validate" && <p className="text-red-500 text-xs">Digite um email válido</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-gray-900 mb-1">Telefone:</label>
                  <input
                    {...register("phone", { required: true })}
                    type="text"
                    disabled={!isReLegalValid}
                    className={`block w-full rounded-2xl px-4 py-0.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 disabled:bg-gray-200
                      ${errors?.phone ? "border border-red-500" : "border border-zinc-500"}
                    `}
                  />
                  {errors?.phone && <p className="text-red-500 text-xs">Telefone é obrigatório</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-gray-900 mb-1">Estado:</label>
                  <select
                    defaultValue="0"
                    {...register("uf", { validate: (value) => value !== "0" })}
                    disabled={!isReLegalValid}
                    className={`block w-full rounded-2xl px-4 py-0.5 text-sm text-gray-900 shadow-sm disabled:bg-gray-200
                      ${errors?.uf ? "border border-red-500" : "border border-zinc-500"}
                    `}
                  >
                    <option value="0" disabled>Escolha um estado</option>
                    {Estados.map((estado, idx) => (
                      <option key={idx} value={estado.sigla}>{estado.nome}</option>
                    ))}
                  </select>
                  {errors?.uf && <p className="text-red-500 text-xs">Estado é obrigatório</p>}
                </div>
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-gray-900 mb-1">Perfil:</label>
                  <select
                    defaultValue="user"
                    {...register("role")}
                    disabled={!isReLegalValid}
                    className={`block w-full rounded-2xl px-4 py-0.5 text-sm text-gray-900 shadow-sm border border-zinc-500 disabled:bg-gray-200`}
                  >
                    <option value="user">Usuário</option>
                    <option value="tecnico">Técnico</option>
                    <option value="warehouse">Warehouse</option>
                    <option value="vivogestao">Vivo Gestão</option>
                    <option value="entregador">Entregador</option>
                    <option value="report-reader">Visualizador de Relatórios</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-gray-900 mb-1">Senha:</label>
                  <input
                    {...register("password", { required: true, minLength: 3 })}
                    type="password"
                    disabled={!isReLegalValid}
                    className={`block w-full rounded-2xl px-4 py-0.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 disabled:bg-gray-200
                      ${errors?.password ? "border border-red-500" : "border border-zinc-500"}
                    `}
                  />
                  {errors?.password?.type === "required" && <p className="text-red-500 text-xs">Senha é obrigatório</p>}
                  {errors?.password?.type === "minLength" && <p className="text-red-500 text-xs">Senha deve ter no mínimo 3 caracteres</p>}
                </div>
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-gray-900 mb-1">Confirmar Senha:</label>
                  <input
                    {...register("confirmpassword", { required: true, validate: (value) => value === watchPassword })}
                    type="password"
                    disabled={!isReLegalValid}
                    className={`block w-full rounded-2xl px-4 py-0.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 disabled:bg-gray-200
                      ${errors?.confirmpassword ? "border border-red-500" : "border border-zinc-500"}
                    `}
                  />
                  {errors?.confirmpassword?.type === "required" && <p className="text-red-500 text-xs">Confirmar Senha é obrigatório</p>}
                  {errors?.confirmpassword?.type === "validate" && <p className="text-red-500 text-xs">As senhas não conferem</p>}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Empresa:</label>
                <select
                  defaultValue="0"
                  {...register("company")}
                  disabled={!isReLegalValid}
                  className={`block w-full rounded-2xl px-4 py-0.5 text-sm text-gray-900 shadow-sm border border-zinc-500 disabled:bg-gray-200`}
                >
                  <option value="0" disabled>Escolha uma empresa</option>
                  {companiesList.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.socialName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-center gap-3 mt-4">
                <button
                  type="submit"
                  disabled={!isReLegalValid}
                  className={`w-full text-sm text-white py-1.5 px-4 rounded-2xl ${isReLegalValid ? "bg-blue-500 hover:bg-blue-600" : "bg-blue-300 cursor-not-allowed"}`}
                >
                  Cadastrar Usuário
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="w-full text-sm bg-gray-500 text-white py-1.5 px-4 rounded-2xl hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}